import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { Sparkles, UserPlus } from "lucide-react";

// The moment a search comes up empty is the most informative moment we get:
// the shopper has told us exactly what the marketplace is missing. Rather than
// ending on "No items found", capture it — as demand (what to recruit for, and
// where) and, better still, as a named neighbor who already makes the thing.

export function RequestItemCard({
  query,
  sessionId,
  zipCode,
}: {
  query: string;
  sessionId?: string;
  zipCode?: string;
}) {
  const [requested, setRequested] = useState(false);
  const [canNotify, setCanNotify] = useState(false);
  const [demandId, setDemandId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [namingMaker, setNamingMaker] = useState(false);
  const [makerName, setMakerName] = useState("");
  const [makerContact, setMakerContact] = useState("");
  const [leadSent, setLeadSent] = useState(false);

  // Fetched up front, not on tap: the share sheet needs the gesture that opened
  // it, and awaiting a fetch first spends it (trap #11).
  const { data: referral } = useQuery<{ shareUrl: string }>({
    queryKey: ["/api/my/referral-code"],
    queryFn: async () => {
      const res = await fetch("/api/my/referral-code", { credentials: "include" });
      if (!res.ok) throw new Error("no referral code");
      return res.json();
    },
    staleTime: 5 * 60_000,
    retry: false,
  });

  const submitRequest = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/demand", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ q: query, sessionId, zipCode }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setDemandId(data?.id ?? null);
      setCanNotify(Boolean(data?.canNotify));
      setRequested(true);
    } catch {
      toast({ description: "Couldn't save that request. Try again." });
    } finally {
      setSaving(false);
    }
  };

  const submitLead = async () => {
    const name = makerName.trim();
    if (!name) return;
    setSaving(true);
    try {
      const res = await fetch("/api/maker-leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          makerName: name,
          makerContact: makerContact.trim() || null,
          demandId,
          sessionId,
          note: `Suggested after searching "${query}"`,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setLeadSent(true);
      setNamingMaker(false);
      toast({
        title: "Thank you",
        description: `We'll reach out to ${name} about selling on Stoop.`,
      });
    } catch {
      toast({ description: "Couldn't save that. Try again." });
    } finally {
      setSaving(false);
    }
  };

  // Sharing the invite directly is the strongest version of this: the neighbor
  // hears it from someone they know, and the sharer earns the maker-invite
  // reward when that maker publishes.
  const shareInvite = () => {
    const url = referral?.shareUrl ? `${referral.shareUrl}&role=maker` : null;
    if (!url) return;
    const text = `Nobody near us is selling ${query} on Stoop yet. If you make it, you can list it here. ${url}`;
    if (typeof navigator !== "undefined" && navigator.share) {
      void navigator.share({ title: "Stoop", text }).catch(() => {});
      return;
    }
    void navigator.clipboard
      ?.writeText(text)
      .then(() => toast({ title: "Invite copied", description: "Paste it into a text to them." }))
      .catch(() => {});
  };

  return (
    <div className="mx-auto mt-6 max-w-md rounded-2xl border border-border bg-card p-4 text-left">
      <h3 className="text-sm font-semibold">
        {requested ? `We noted that you want ${query}` : `Nobody's selling ${query} yet`}
      </h3>
      <p className="mt-1 text-xs text-muted-foreground">
        {requested
          ? canNotify
            ? "We'll let you know as soon as a neighbor lists it."
            : "Sign in and we can tell you when a neighbor lists it."
          : "Tell us you want it and we'll use it to bring the right makers on. Every listing here started with someone asking."}
      </p>

      {!requested && (
        <Button size="sm" className="mt-3 w-full" onClick={submitRequest} disabled={saving}>
          Request {query}
        </Button>
      )}

      {!leadSent && !namingMaker && (
        <Button
          size="sm"
          variant="outline"
          className="mt-2 w-full gap-2"
          onClick={() => setNamingMaker(true)}
        >
          <UserPlus className="h-3.5 w-3.5" />
          I know someone who makes it
        </Button>
      )}

      {namingMaker && (
        <div className="mt-3 space-y-2">
          <Input
            autoFocus
            value={makerName}
            onChange={(e) => setMakerName(e.target.value)}
            placeholder="Their name or business"
          />
          <Input
            value={makerContact}
            onChange={(e) => setMakerContact(e.target.value)}
            placeholder="Phone, email or Instagram (optional)"
          />
          <div className="flex gap-2">
            <Button size="sm" className="flex-1" onClick={submitLead} disabled={saving || !makerName.trim()}>
              Send to Stoop
            </Button>
            <Button size="sm" variant="outline" onClick={() => setNamingMaker(false)}>
              Cancel
            </Button>
          </div>
          {referral?.shareUrl && (
            <button
              type="button"
              onClick={shareInvite}
              className="flex w-full items-center justify-center gap-1.5 pt-1 text-xs font-medium text-primary"
            >
              <Sparkles className="h-3 w-3" />
              Or invite them yourself
            </button>
          )}
        </div>
      )}
    </div>
  );
}
